import { faCopy } from "@fortawesome/free-solid-svg-icons";
import React, { type FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { v4 as uuid } from "uuid";
import { useApiCall } from "../api/api";
import type { Company, Exam, Project } from "../api/dto";
import { Input } from "../components/form/Input";
import { Select } from "../components/form/Select";
import { ModalFooter } from "../components/layout/ModalProvider";
import { useRefresh } from "../components/RefreshController";

export const CloneExamModal: FC<{
  companies: Company[];
  projects: Project[];
  exams: Exam[];
  exam: Exam;
}> = (props) => {
  const { addOrUpdateExam } = useApiCall();
  const reload = useRefresh();
  const navigate = useNavigate();

  // Company
  const [company, setCompany] = useState<Company["id"]>(props.exam.companyId);
  const companyOptions = Object.fromEntries([
    ["-", "Selecteer bedrijf"],
    ...props.companies.filter((c) => !c.isArchived).map((c) => [c.id, c.name]),
  ]);

  // Project
  const [project, setProject] = useState<Project["id"]>(props.exam.projectId);
  const projectOptions = Object.fromEntries([
    ["-", "Selecteer project"],
    ...props.projects
      .filter((p) => !p.isArchived)
      .filter((p) => p.companyId === company)
      .map((p) => [p.id, p.name]),
  ]);
  useEffect(() => {
    if (!Object.keys(projectOptions).includes(project)) {
      setProject("-");
    }
  }, [project, projectOptions]);

  // Exam name
  const [name, setName] = useState<string>(props.exam.name ?? "");
  const examNames = Array.from(new Set(props.exams.map((e) => e.name ?? "-")));
  const isUnique = !examNames.includes(name);

  const confirm = async () => {
    const newId = uuid();
    await addOrUpdateExam(newId, {
      company_id: company,
      project_id: project,
      question_set_id: props.exam.questionSetId,
      assessor_id: props.exam.assessorId,
      name: name,
    });
    reload();
    navigate(`/exams/${newId}`);
  };

  return (
    <>
      <p>
        <strong>Let op!</strong> Dit zet een nieuw examen klaar zonder deelnemers, maar op basis van
        de zelfde gegevens als dit examen.
      </p>
      <div className={"grid grid-cols-1 gap-3 mt-3"}>
        <Select options={companyOptions} value={company} onChange={setCompany} label={"Bedrijf"} />
        <Select options={projectOptions} value={project} onChange={setProject} label={"Project"} />
        <Input type={"text"} label={"Examennaam"} value={name} onChange={setName} />
        {isUnique ? null : <p className={"text-red-500"}>Deze examennaam is al in gebruik.</p>}
      </div>
      <ModalFooter
        icon={faCopy}
        text={"Dupliceren"}
        onSubmit={confirm}
        disabled={company === "-" || project === "-" || !isUnique}
      />
    </>
  );
};
